import { Person, PersonRole } from "@/features/auth/graphql/generated";
import { ResourceEnum } from "@/shared/constants/resources";
import { AccessLevel, RoleType } from "@/shared/constants/roles";
import { find } from "lodash";

type AllowedAccessLevelsParams = {
  resourceType: ResourceEnum;
  role?: RoleType | null;
  canAssignFullAccessLevel: boolean;
  /**
   * TA-145: project CANDIDATEs may hold READ/FULL access, but only when the person
   * is already a workspace/org member — external candidates stay NONE-only.
   * Callers that know the person should pass this (see getIsWorkspaceMember);
   * defaults to false so unknown/new people are never offered access as candidates.
   */
  candidateCanHoldAccess?: boolean;
};

type NormalizeRoleAccessParams = {
  resourceType: ResourceEnum;
  role?: RoleType | null;
  accessLevel?: AccessLevel | null;
  canAssignFullAccessLevel: boolean;
};

type NormalizedRoleAccess = {
  role: RoleType;
  accessLevel: AccessLevel;
};

export const getPersonRole = (
  data: Person,
  resource: string,
  resourceId: number
) => {
  return find(
    data?.roles,
    (role) => role?.type === resource && role?.obj_id === resourceId
  )?.role;
};

export const getPersonAccessLevel = (
  data: Person,
  resource: string,
  resourceId: number
) => {
  return find(
    data?.roles,
    (role) => role?.type === resource && role?.obj_id === resourceId
  )?.access_level;
};

export const getPersonRolesAndAccess = (
  data: Person,
  resource: string,
  resourceId: number
) => {
  return {
    role: getPersonRole(data, resource, resourceId),
    accessLevel: getPersonAccessLevel(data, resource, resourceId),
  };
};

/**
 * Find role by resource type and id
 */
export const getPersonRoleObject = (
  roles: PersonRole[],
  resourceType: ResourceEnum,
  resourceId: number
): PersonRole | undefined => {
  return roles.find(
    (r: PersonRole) => r?.type === resourceType && r?.obj_id === resourceId
  );
};

/**
 * Allowed role values by resource type (UI selection scope).
 * This matches PERMISSIONS_SYSTEM.md "Roles by Resource Type" for editable contexts.
 */
export const getAllowedRoles = (params: { resourceType: ResourceEnum }) => {
  const { resourceType } = params;

  if (resourceType === ResourceEnum.account) {
    return [RoleType.member, RoleType.admin, RoleType.owner] as RoleType[];
  }

  if (resourceType === ResourceEnum.organization) {
    return [RoleType.member, RoleType.candidate, RoleType.admin] as RoleType[];
  }

  if (resourceType === ResourceEnum.team) {
    return [RoleType.member, RoleType.collaborator] as RoleType[];
  }

  if (resourceType === ResourceEnum.project) {
    // Project role selection is generally not editable in existing UI, but keep for completeness.
    return [RoleType.candidate, RoleType.collaborator] as RoleType[];
  }

  return undefined;
};

const getDefaultRoleForResource = (resourceType: ResourceEnum): RoleType => {
  switch (resourceType) {
    case ResourceEnum.project:
      return RoleType.collaborator;
    case ResourceEnum.team:
      return RoleType.member;
    case ResourceEnum.organization:
      return RoleType.member;
    case ResourceEnum.account:
    default:
      return RoleType.member;
  }
};

const clampToAllowedAccessLevels = (
  desired: AccessLevel,
  allowed: AccessLevel[]
): AccessLevel => {
  if (allowed.includes(desired)) return desired;

  // Prefer least-privilege fallback that is still valid.
  if (allowed.includes(AccessLevel.read)) return AccessLevel.read;
  if (allowed.includes(AccessLevel.limited)) return AccessLevel.limited;
  if (allowed.includes(AccessLevel.none)) return AccessLevel.none;
  if (allowed.includes(AccessLevel.edit)) return AccessLevel.edit;
  if (allowed.includes(AccessLevel.full)) return AccessLevel.full;

  // Should never happen; keep original.
  return desired;
};

/**
 * Compute allowed access levels for a given (resourceType, role).
 * This is derived from PERMISSIONS_SYSTEM.md "Access Levels" + role constraints.
 */
export const getAllowedAccessLevels = (
  params: AllowedAccessLevelsParams
): AccessLevel[] | undefined => {
  const { resourceType, canAssignFullAccessLevel } = params;
  const role = params.role || getDefaultRoleForResource(resourceType);

  const restrictFull = !canAssignFullAccessLevel;
  const maybeDropFull = (levels: AccessLevel[]) =>
    restrictFull ? levels.filter((l) => l !== AccessLevel.full) : levels;

  // Workspace(Account) access levels: NONE, LIMITED, EDIT, FULL
  if (resourceType === ResourceEnum.account) {
    switch (role) {
      case RoleType.owner:
        return [AccessLevel.full];
      case RoleType.admin:
        return maybeDropFull([AccessLevel.edit, AccessLevel.full]);
      case RoleType.member:
        return [AccessLevel.limited];
      case RoleType.candidate:
        return [AccessLevel.none, AccessLevel.limited];
      case RoleType.contact:
        return [AccessLevel.none];
      default:
        return undefined;
    }
  }

  // Organization access levels: NONE, LIMITED, EDIT, FULL (FULL => ADMIN)
  if (resourceType === ResourceEnum.organization) {
    switch (role) {
      case RoleType.admin:
        return [AccessLevel.full];
      case RoleType.member:
        return maybeDropFull([
          AccessLevel.limited,
          AccessLevel.edit,
          AccessLevel.full,
        ]);
      case RoleType.candidate:
        return [AccessLevel.none, AccessLevel.limited];
      default:
        return undefined;
    }
  }

  // Team access levels: NONE, READ, FULL (COLLABORATOR cannot be NONE)
  if (resourceType === ResourceEnum.team) {
    switch (role) {
      case RoleType.member:
        return maybeDropFull([
          AccessLevel.none,
          AccessLevel.read,
          AccessLevel.full,
        ]);
      case RoleType.collaborator:
        return maybeDropFull([AccessLevel.read, AccessLevel.full]);
      default:
        return undefined;
    }
  }

  // Project access levels: NONE, READ, FULL
  // TA-145: CANDIDATE may hold READ/FULL when the person is an internal member
  // (candidacy is decoupled from access); external candidates are NONE only.
  if (resourceType === ResourceEnum.project) {
    switch (role) {
      case RoleType.candidate:
        return params.candidateCanHoldAccess
          ? maybeDropFull([
              AccessLevel.none,
              AccessLevel.read,
              AccessLevel.full,
            ])
          : [AccessLevel.none];
      case RoleType.collaborator:
        return maybeDropFull([AccessLevel.read, AccessLevel.full]);
      default:
        return undefined;
    }
  }

  return undefined;
};

/**
 * Normalize role/access to ensure we never end up in invalid combinations.
 * Returns a fully-defined `{ role, accessLevel }` pair.
 *
 * Notes:
 * - Org FULL implies ADMIN (doc: automatic promotion)
 * - If FULL can't be assigned, clamps to the nearest valid access level
 */
export const normalizeRoleAccess = (
  params: NormalizeRoleAccessParams
): NormalizedRoleAccess => {
  const { resourceType, canAssignFullAccessLevel } = params;
  let role = (params.role ||
    getDefaultRoleForResource(resourceType)) as RoleType;

  // Provide a resource-specific default access level if missing.
  let accessLevel = (params.accessLevel ||
    (() => {
      switch (resourceType) {
        case ResourceEnum.account:
          return AccessLevel.limited;
        case ResourceEnum.organization:
          return AccessLevel.limited;
        case ResourceEnum.team:
          return AccessLevel.read;
        case ResourceEnum.project:
          return role === RoleType.candidate
            ? AccessLevel.none
            : AccessLevel.read;
        default:
          return AccessLevel.limited;
      }
    })()) as AccessLevel;

  // Derived / implied role transitions from doc constraints.
  if (resourceType === ResourceEnum.account) {
    /**
     * PERMISSIONS_SYSTEM.md: access downgrades can force role demotion at workspace level.
     *
     * - OWNER downgraded FULL -> EDIT: stored role demotes to ADMIN with EDIT access
     * - OWNER downgraded FULL -> LIMITED: stored role demotes to MEMBER with LIMITED access
     * - ADMIN downgraded FULL -> LIMITED: stored role demotes to MEMBER with LIMITED access
     *
     * Notes:
     * - The backend is the source of truth and must enforce these write-time mutations.
     * - This normalization exists to keep UI role/access selection consistent with doc constraints.
     */
    if (accessLevel === AccessLevel.limited) {
      if (role === RoleType.owner || role === RoleType.admin) {
        role = RoleType.member;
      }
    } else if (accessLevel === AccessLevel.edit) {
      if (role === RoleType.owner) {
        role = RoleType.admin;
      }
    }
  }

  if (resourceType === ResourceEnum.organization) {
    // FULL implies ADMIN; non-FULL implies MEMBER (ADMIN is a derived state at org level).
    // This allows "demotion" by lowering access level (ADMIN -> MEMBER).
    if (accessLevel === AccessLevel.full) role = RoleType.admin;
    else if (role === RoleType.admin) role = RoleType.member;
  }

  // If role implies fixed access levels, clamp (e.g., OWNER always FULL).
  const allowed = getAllowedAccessLevels({
    resourceType,
    role,
    canAssignFullAccessLevel,
  });
  if (allowed?.length) {
    accessLevel = clampToAllowedAccessLevels(accessLevel, allowed);
  }

  // If clamping produced FULL in org context, ensure role is ADMIN.
  if (
    resourceType === ResourceEnum.organization &&
    accessLevel === AccessLevel.full
  ) {
    role = RoleType.admin;
  }

  return { role, accessLevel };
};

/**
 * Determine if access level can be edited based on PERMISSIONS_SYSTEM.md rules
 *
 * @param role - The person's role object
 * @param resourceType - Type of resource (ACCOUNT, ORGANIZATION, TEAM, PROJECT)
 * @returns boolean indicating if the access level can be changed
 */
export const getIsAccessLevelEditable = (
  role: PersonRole,
  resourceType: ResourceEnum
): boolean => {
  if (!role) return false;

  // Workspace(Account) level:
  // - OWNER is always FULL (not editable)
  // - MEMBER is always LIMITED (not editable)
  // - CONTACT is always NONE (not editable)
  // - CANDIDATE access is primarily system-driven (treat as not editable here)
  // - ADMIN/OWNER can toggle EDIT/FULL
  // - MEMBER can be promoted by upgrading access (backend enforces invariants and may auto-promote role)
  if (resourceType === ResourceEnum.account) {
    // PERMISSIONS_SYSTEM.md: demotion-by-access is allowed for ADMIN/OWNER via access downgrade.
    return (
      role?.role === RoleType.admin ||
      role?.role === RoleType.owner ||
      role?.role === RoleType.member
    );
  }

  // Organization level:
  // - ADMIN is always FULL (not editable)
  // - MEMBER can be LIMITED/EDIT/FULL (editable)
  // - CANDIDATE may be NONE/LIMITED (editable)
  if (resourceType === ResourceEnum.organization) {
    // PERMISSIONS_SYSTEM.md: lowering access from FULL demotes ADMIN -> MEMBER (write-time).
    return (
      role?.role === RoleType.member ||
      role?.role === RoleType.admin ||
      role?.role === RoleType.candidate
    );
  }

  // Team level:
  // - MEMBER: NONE/READ/FULL
  // - COLLABORATOR: READ/FULL
  if (resourceType === ResourceEnum.team) {
    return (
      role?.role === RoleType.member || role?.role === RoleType.collaborator
    );
  }

  // Project level:
  // - COLLABORATOR: READ/FULL
  // - CANDIDATE: NONE (not editable)
  if (resourceType === ResourceEnum.project) {
    return role?.role === RoleType.collaborator;
  }

  return false;
};

/**
 * Determine if role can be edited based on PERMISSIONS_SYSTEM.md rules and UI constraints.
 *
 * Primary intent:
 * - Workspace contacts shown in the Organization people page must not be editable
 *   (they are coerced to RoleType.contact in `useSearchTalentPool.ts`).
 */
export const getIsRoleEditable = (
  role: PersonRole,
  resourceType: ResourceEnum
): boolean => {
  if (!role) return false;

  // CONTACT users are system-managed; they cannot be promoted directly via role switching.
  if (role?.role === RoleType.contact) return false;

  // Workspace(Account) roles:
  // Keep OWNER non-editable in UI; other roles can be changed if caller has permission.
  if (resourceType === ResourceEnum.account) {
    return role?.role !== RoleType.owner;
  }

  // Organization roles:
  // ADMIN is implied by FULL access and not intended to be toggled via role selector.
  if (resourceType === ResourceEnum.organization) {
    return role?.role === RoleType.member || role?.role === RoleType.candidate;
  }

  if (resourceType === ResourceEnum.team) {
    return (
      role?.role === RoleType.member || role?.role === RoleType.collaborator
    );
  }

  if (resourceType === ResourceEnum.project) {
    return (
      role?.role === RoleType.candidate || role?.role === RoleType.collaborator
    );
  }

  return false;
};

/**
 * TA-145: whether the person is an internal workspace member (MEMBER/ADMIN/OWNER).
 * Used to gate candidate access options — external people (workspace CONTACT/
 * CANDIDATE) can never hold project access as candidates. The backend
 * additionally enforces org-level membership for the project's organization.
 */
export const getIsWorkspaceMember = (roles?: PersonRole[] | null): boolean => {
  const accountRole = roles?.find((r) => r?.type === ResourceEnum.account);
  return (
    accountRole?.role === RoleType.member ||
    accountRole?.role === RoleType.admin ||
    accountRole?.role === RoleType.owner
  );
};

export const getAccessLevelFilter = (
  role: PersonRole,
  resourceType: ResourceEnum,
  canChangeFullAccessLevel: boolean,
  personRoles?: PersonRole[] | null
) => {
  /**
   * IMPORTANT:
   * Backend grantables remain the source of truth; this is only a fallback list.
   * Some roles have access-driven demotion/promotion rules that would otherwise make the
   * fallback too restrictive (e.g., org ADMIN is typically FULL-only, but can be downgraded).
   */
  if (
    resourceType === ResourceEnum.organization &&
    role?.role === RoleType.admin
  ) {
    return [
      AccessLevel.limited,
      AccessLevel.edit,
      ...(canChangeFullAccessLevel ? [AccessLevel.full] : []),
    ];
  }

  if (
    resourceType === ResourceEnum.account &&
    (role?.role === RoleType.admin || role?.role === RoleType.owner)
  ) {
    return [
      AccessLevel.limited,
      AccessLevel.edit,
      ...(canChangeFullAccessLevel ? [AccessLevel.full] : []),
    ];
  }

  if (resourceType === ResourceEnum.account && role?.role === RoleType.member) {
    // Allow promoting a workspace member by upgrading access (backend enforces role/access invariants).
    return [
      AccessLevel.limited,
      AccessLevel.edit,
      ...(canChangeFullAccessLevel ? [AccessLevel.full] : []),
    ];
  }

  return getAllowedAccessLevels({
    resourceType,
    role: role?.role as RoleType,
    canAssignFullAccessLevel: canChangeFullAccessLevel,
    candidateCanHoldAccess:
      resourceType === ResourceEnum.project &&
      getIsWorkspaceMember(personRoles),
  });
};

export const getRoleFilter = (role: PersonRole, resourceType: ResourceEnum) => {
  return getAllowedRoles({ resourceType });
};
